import {
  Button,
  Container,
  Flex,
  Link,
  LoadingIcon,
  Message,
  Stack,
  Text,
} from "basis";
import { getDirectDebit } from "library/api";
import { useQuery } from "react-query";
import { SvgInline } from "../SvgInline/SvgInline";
import { CANCEL_CLICKED } from "./constants";
import { DirectDebitDetails } from "./DirectDebitDetails";

export const DirectDebitLandingContent = ({
  dispatch,
  isOverdue,
  cardToken,
}) => {
  const directDebitInfo = useQuery(
    ["direct-debit", { cardToken }],
    () => getDirectDebit({ token: cardToken }),
    {
      enabled: !isOverdue && cardToken !== undefined,
    },
  );

  if (isOverdue) {
    return (
      <Stack gap="8">
        <SvgInline name="direct-debit-payment-cancel" size="92" />
        <Container bg="secondary.lightBlue.t25" padding="4">
          <Text textStyle="heading5" textStyle-sm="heading4">
            Sorry, we're unable to set up your direct debit request
          </Text>
          <Text margin="6 0 0 0">
            We've noticed that your account is overdue. Please make a one time
            payment to get it up to date before setting up a direct debit
            request.
          </Text>
        </Container>
        <Link appearance="primary-button" href="/ways-to-pay" newTab={false}>
          One time payment
        </Link>
      </Stack>
    );
  }

  if (directDebitInfo.isLoading) {
    return (
      <Flex height="200" placeItems="center">
        <LoadingIcon size="medium" />
      </Flex>
    );
  }

  const directDebitData = directDebitInfo.data?.data;
  const directDebitExists =
    // Will be undefined if account is not selected yet
    directDebitData !== undefined &&
    // Will be null if the selected account doesn't have a direct debit set up
    directDebitData !== null &&
    // We are treating suspended as cancelled or doesn't have a direct debit set up
    directDebitData.payment_type !== "SUSPENDED";

  return (
    <Stack gap="6" flatten>
      {directDebitInfo.isError && (
        <Message severity="warning-or-significant" bg="grey.t05">
          Unable to retrieve the status of your direct debit arrangement.
        </Message>
      )}
      <SvgInline name="direct-debit-payment" size="92" />
      <Text as="h3" textStyle="heading5" textStyle-sm="heading4">
        {directDebitExists
          ? "Your existing direct debit details"
          : "Set up direct debit request"}
      </Text>
      {directDebitExists ? (
        <>
          <DirectDebitDetails
            bsb={directDebitData.bsb}
            accountNumber={directDebitData.account_number}
            paymentType={directDebitData.payment_type}
            paymentAmount={directDebitData.amount}
            prfi={directDebitData.payment_type === "PRFI"}
          />
          <Button type="submit" width="256">
            Update direct debit
          </Button>
          <Container bg="grey.t05" padding="4">
            <Text>
              <strong>Cancel existing direct debit</strong>
            </Text>
            <Text margin="3 0 0 0">
              <Link
                href=""
                newTab={false}
                onClick={(e) => {
                  e.preventDefault();
                  dispatch({
                    type: CANCEL_CLICKED,
                  });
                }}
              >
                Cancelling your direct debit
              </Link>{" "}
              will stop the selected monthly payment amount being automatically
              debited from your nominated bank account on the due date.
            </Text>
          </Container>
        </>
      ) : (
        <>
          <Text>
            Setting up a direct debit request will allow us to create a
            recurring payment from your nominated bank account to help ensure
            that you do not miss a payment and risk any late fees.
          </Text>
          <Button type="submit" width="264">
            Set up direct debit request
          </Button>
        </>
      )}
    </Stack>
  );
};
